import type { PieChartProps } from '@mantine/charts';
import { PieChart as MantinePieChart } from '@mantine/charts';
import { Box, Group, HoverCard, List, Paper, Stack, Text, useMantineTheme } from '@mantine/core';
import { type ReactNode, useMemo } from 'react';
import type {
	DefaultLegendContentProps,
	PieLabelRenderProps,
	PieProps,
	TooltipContentProps,
} from 'recharts';
import { Legend, ResponsiveContainer, Tooltip } from 'recharts';

interface BaseChartData {
	name: string;
	value: number;
	tooltip?: ReactNode;
}

interface DefaultPieChartProps<T extends BaseChartData> extends Omit<PieChartProps, 'data'> {
	data: T[];
	groupBelowPercent?: number;
	groupLabel?: string;
	legendPosition?: 'bottom' | 'right';
}

const CustomTooltip = ({ active, payload }: TooltipContentProps) => {
	if (!active || !payload?.[0]) return null;

	const data = payload[0].payload as BaseChartData;

	return (
		<Paper shadow='md' p='xs' radius='md' withBorder>
			<Text size='sm' fw={500}>
				{data.name}
			</Text>
			<Text size='sm' c='dimmed'>
				{data.tooltip ?? data.value}
			</Text>
		</Paper>
	);
};

const OtherGroupTooltip = ({ items }: { items: BaseChartData[] }) => (
	<List spacing={4} size='sm'>
		{items.map(item => (
			<List.Item key={item.name}>
				<Stack gap={2}>
					<Text fw={500} size='sm'>
						{item.name}
					</Text>
					<Text size='sm' c='dimmed'>
						{item.tooltip ?? item.value}
					</Text>
				</Stack>
			</List.Item>
		))}
	</List>
);

function CustomLegendContent({ payload }: DefaultLegendContentProps) {
	const items = payload ?? [];

	return (
		<Stack gap='md'>
			{items.map((entry, index) => {
				const datum = entry.payload as unknown as BaseChartData & { color?: string };

				return (
					<HoverCard key={index} withArrow shadow='md' openDelay={80}>
						<HoverCard.Target>
							<Group gap={8} style={{ cursor: 'default' }}>
								<Box
									style={{
										width: 12,
										height: 12,
										borderRadius: 3,
										background: entry.color,
										outline: '1px solid rgba(0,0,0,0.15)',
									}}
								/>
								<Text size='sm'>{entry.value}</Text>
							</Group>
						</HoverCard.Target>

						<HoverCard.Dropdown p='xs'>
							<Stack gap={2}>
								<Text fw={500} size='sm'>
									{datum.name}
								</Text>
								<Text size='sm' c='dimmed'>
									{datum.tooltip ?? datum.value}
								</Text>
							</Stack>
						</HoverCard.Dropdown>
					</HoverCard>
				);
			})}
		</Stack>
	);
}

const DEGREES_TO_RADIANS = Math.PI / 180;

function renderOuterInnerLabel({ cx, cy, midAngle, outerRadius, percent }: PieLabelRenderProps) {
	if (!percent || !cx || !cy || !outerRadius || !midAngle) {
		return null;
	}

	const radius = Number(outerRadius) - 20;
	const x = Number(cx) + radius * Math.cos(-midAngle * DEGREES_TO_RADIANS);
	const y = Number(cy) + radius * Math.sin(-midAngle * DEGREES_TO_RADIANS);

	return (
		<text x={x} y={y} textAnchor='middle' dominantBaseline='central' fontSize={16}>
			{Math.round(percent * 100)}%
		</text>
	);
}

export function DefaultPieChart<T extends BaseChartData>({
	data,
	withTooltip,
	groupBelowPercent,
	groupLabel = 'Other',
	legendPosition = 'bottom',
	...props
}: DefaultPieChartProps<T>) {
	const theme = useMantineTheme();

	const groupedData = useMemo(() => {
		if (!groupBelowPercent || groupBelowPercent <= 0 || data.length === 0) {
			return data;
		}

		const total = data.reduce((sum, item) => sum + (item.value ?? 0), 0);
		if (total === 0) return data;

		const threshold = (groupBelowPercent / 100) * total;
		const smallItems = data.filter(item => item.value < threshold);
		const largeItems = data.filter(item => item.value >= threshold);

		if (smallItems.length <= 1) return data;

		const otherSlice = {
			name: groupLabel,
			value: smallItems.reduce((sum, item) => sum + item.value, 0),
			tooltip: <OtherGroupTooltip items={smallItems as BaseChartData[]} />,
		};

		return [...largeItems, otherSlice];
	}, [data, groupBelowPercent, groupLabel]);

	const OTHER_COLOR = theme.colors.gray[5];
	const pieChartColors = useMemo(() => {
		const baseColors = [
			theme.colors.blue,
			theme.colors.green,
			theme.colors.cyan,
			theme.colors.indigo,
			theme.colors.teal,
			theme.colors.orange,
			theme.colors.pink,
			theme.colors.red,
			theme.colors.yellow,
		];
		const shades = [3, 4, 5, 6, 7];
		return shades.flatMap(shade => baseColors.map(color => color[shade]));
	}, [theme]);

	const formattedData = useMemo(
		() =>
			groupedData.map((item, index) => {
				const baseColor =
					pieChartColors[index % pieChartColors.length] ??
					'var(--mantine-primary-color-contrast)';

				return {
					...item,
					color: item.name === groupLabel ? OTHER_COLOR : baseColor,
				};
			}),
		[groupedData, pieChartColors, OTHER_COLOR, groupLabel],
	);

	const pieProps: PieProps = useMemo(
		() => ({
			dataKey: 'value',
			isAnimationActive: true,
			animationDuration: 1000,
			label: renderOuterInnerLabel,
			labelLine: false,
		}),
		[],
	);

	const legendProps = useMemo(
		() =>
			legendPosition === 'right'
				? ({
						layout: 'vertical',
						align: 'right',
						verticalAlign: 'middle',
						wrapperStyle: { right: 0 },
						fontSize: '1rem',
					} as const)
				: ({
						layout: 'horizontal',
						align: 'center',
						verticalAlign: 'bottom',
						fontSize: '2rem',
					} as const),
		[legendPosition],
	);

	return (
		<ResponsiveContainer width='100%' height={450}>
			<MantinePieChart data={formattedData} {...props} pieProps={pieProps}>
				<Legend {...legendProps} content={CustomLegendContent} />
				{withTooltip && <Tooltip content={CustomTooltip} />}
			</MantinePieChart>
		</ResponsiveContainer>
	);
}
